import React from "react";
import get from "lodash/get";
import { Toggle } from "react-powerplug";
import {
  Button,
  Card,
  Form,
  Input,
  Checkbox,
  Icon,
  InputNumber,
  Popconfirm,
} from "antd";
import { Draggable, Droppable } from "react-beautiful-dnd";

import HTMLEditor from "./HTMLEditor";
import EventQuestionMetricsContainer from "./EventQuestionMetricsContainer";

const EventQuestionEditor = ({
  event,
  form,
  getEventFieldName,
  getEventDroppableId,
  getEventDraggableId,
  onAddQuestionChoiceRequested,
  onRemoveQuestionChoiceRequested,
  onAddQuestionMetricRequested,
  onRemoveQuestionMetricRequested,
}) => (
  <React.Fragment>
    <Form.Item
      label="Question"
      labelCol={{ span: 3 }}
      wrapperCol={{ span: 21 }}
    >
      <Toggle initial={false}>
        {({ on: editRaw, toggle }) => (
          <React.Fragment>
            {!editRaw
              ? form.getFieldDecorator(getEventFieldName(event, "text"), {
                  initialValue: get(event, "text", ""),
                })(<HTMLEditor />)
              : form.getFieldDecorator(getEventFieldName(event, "text"), {
                  initialValue: get(event, "text", ""),
                })(<Input.TextArea autoSize={true} />)}
            <Checkbox onClick={toggle} checked={editRaw}>
              Edit raw HTML
            </Checkbox>
          </React.Fragment>
        )}
      </Toggle>
    </Form.Item>

    <Form.Item
      label="Time bonus"
      labelCol={{ span: 3 }}
      wrapperCol={{ span: 21 }}
    >
      {form.getFieldDecorator(getEventFieldName(event, "time_bonus"), {
        initialValue: get(event, "time_bonus"),
      })(<InputNumber />)}
    </Form.Item>

    <Form.Item label="Show choices immediately">
      {form.getFieldDecorator(
        getEventFieldName(event, "show_choices_immediately"),
        {
          validateTrigger: ["onBlur"],
          valuePropName: "checked",
          initialValue: !!get(event, "show_choices_immediately"),
        }
      )(<Input type="checkbox" />)}
    </Form.Item>

    <Form.Item label="Time" labelCol={{ span: 3 }} wrapperCol={{ span: 21 }}>
      {form.getFieldDecorator(getEventFieldName(event, "choices_end_delay"), {
        initialValue: get(event, "choices_end_delay"),
      })(<InputNumber />)}
    </Form.Item>

    <Form.Item label="Screen text to show instead of choices">
      <Toggle initial={false}>
        {({ on: editRaw, toggle }) => (
          <React.Fragment>
            {!editRaw
              ? form.getFieldDecorator(
                  getEventFieldName(event, "screen_text_choices"),
                  {
                    initialValue: get(event, "screen_text_choices", ""),
                  }
                )(<HTMLEditor />)
              : form.getFieldDecorator(
                  getEventFieldName(event, "screen_text_choices"),
                  {
                    initialValue: get(event, "screen_text_choices", ""),
                  }
                )(<Input.TextArea autoSize={true} />)}
            <Checkbox onClick={toggle} checked={editRaw}>
              Edit raw HTML
            </Checkbox>
          </React.Fragment>
        )}
      </Toggle>
    </Form.Item>

    <Form.Item label="Screen text to show instead of statistics">
      <Toggle initial={false}>
        {({ on: editRaw, toggle }) => (
          <React.Fragment>
            {!editRaw
              ? form.getFieldDecorator(
                  getEventFieldName(event, "screen_text_statistics"),
                  {
                    initialValue: get(event, "screen_text_statistics", ""),
                  }
                )(<HTMLEditor />)
              : form.getFieldDecorator(
                  getEventFieldName(event, "screen_text_statistics"),
                  {
                    initialValue: get(event, "screen_text_statistics", ""),
                  }
                )(<Input.TextArea autoSize={true} />)}
            <Checkbox onClick={toggle} checked={editRaw}>
              Edit raw HTML
            </Checkbox>
          </React.Fragment>
        )}
      </Toggle>
    </Form.Item>

    <Form.Item label="Choices">
      <Droppable
        droppableId={getEventDroppableId(event, "choices")}
        type="question-choice"
      >
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              backgroundColor: "#ccc",
              padding: "5px",
            }}
          >
            {get(event, "choices", []).map((choice, choiceIndex) => {
              const draggableId = getEventDraggableId(event, choiceIndex);
              return (
                <Draggable
                  key={draggableId}
                  draggableId={draggableId}
                  index={choiceIndex}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <Card
                        size="small"
                        extra={
                          <Popconfirm
                            onConfirm={() =>
                              onRemoveQuestionChoiceRequested(
                                event,
                                choiceIndex
                              )
                            }
                            title="Do you really want to delete this choice?"
                            cancelText="No"
                            okText="Yes"
                          >
                            <Button>
                              <Icon type="delete" />
                            </Button>
                          </Popconfirm>
                        }
                      >
                        <Form.Item
                          label="Text"
                          labelCol={{ span: 3 }}
                          wrapperCol={{ span: 21 }}
                        >
                          {form.getFieldDecorator(
                            getEventFieldName(
                              event,
                              `choices[${choiceIndex}].content`
                            ),
                            {
                              initialValue: get(
                                event,
                                `choices[${choiceIndex}].content`
                              ),
                            }
                          )(<Input />)}
                        </Form.Item>
                        <Form.Item
                          label="Short text"
                          labelCol={{ span: 3 }}
                          wrapperCol={{ span: 21 }}
                        >
                          {form.getFieldDecorator(
                            getEventFieldName(
                              event,
                              `choices[${choiceIndex}].short_text`
                            ),
                            {
                              initialValue: get(
                                event,
                                `choices[${choiceIndex}].short_text`
                              ),
                            }
                          )(<Input />)}
                        </Form.Item>
                        <Form.Item
                          label="Hint"
                          labelCol={{ span: 3 }}
                          wrapperCol={{ span: 21 }}
                        >
                          {form.getFieldDecorator(
                            getEventFieldName(
                              event,
                              `choices[${choiceIndex}].hint`
                            ),
                            {
                              initialValue: get(
                                event,
                                `choices[${choiceIndex}].hint`
                              ),
                            }
                          )(<Input />)}
                        </Form.Item>
                        <Form.Item
                          label="Message"
                          labelCol={{ span: 3 }}
                          wrapperCol={{ span: 21 }}
                        >
                          {form.getFieldDecorator(
                            getEventFieldName(
                              event,
                              `choices[${choiceIndex}].message`
                            ),
                            {
                              initialValue: get(
                                event,
                                `choices[${choiceIndex}].message`
                              ),
                            }
                          )(
                            <Input placeholder="Enter a custom message to override the choice response" />
                          )}
                        </Form.Item>

                        <Form.Item
                          label="Points"
                          labelCol={{ span: 3 }}
                          wrapperCol={{ span: 21 }}
                        >
                          {form.getFieldDecorator(
                            getEventFieldName(
                              event,
                              `choices[${choiceIndex}].points`
                            ),
                            {
                              initialValue: get(
                                event,
                                `choices[${choiceIndex}].points`
                              ),
                            }
                          )(<InputNumber />)}
                        </Form.Item>

                        <Form.Item
                          label="Object ID"
                          labelCol={{ span: 3 }}
                          wrapperCol={{ span: 21 }}
                        >
                          {form.getFieldDecorator(
                            getEventFieldName(
                              event,
                              `choices[${choiceIndex}].object_id`
                            ),
                            {
                              initialValue: get(
                                event,
                                `choices[${choiceIndex}].object_id`
                              ),
                            }
                          )(
                            <Input placeholder="Put a valid object id here and it will be added to the player's inventory" />
                          )}
                        </Form.Item>

                        <Form.Item
                          label="Metrics"
                          labelCol={{ span: 3 }}
                          wrapperCol={{ span: 21 }}
                        >
                          <EventQuestionMetricsContainer
                            {...{
                              event,
                              choiceIndex,
                              form,
                              getEventFieldName,
                              onAddQuestionMetricRequested,
                              onRemoveQuestionMetricRequested,
                            }}
                          />
                        </Form.Item>
                      </Card>
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      <Button onClick={() => onAddQuestionChoiceRequested(event)}>
        <Icon type="plus" />
        Add choice
      </Button>
    </Form.Item>
  </React.Fragment>
);

export default EventQuestionEditor;
